import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { initializeApp, getApps } from 'firebase/app';
import { firebaseConfig, firebaseFunctionsConfig, getEnvironment } from '../config/firebase';
import { User, Organization, Team, Challenge } from '../types/admin';

// Using shared types from ../types/admin

// Initialize Firebase app
const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

// Helper function to ensure user is authenticated
const ensureAuthenticated = async () => {
  const auth = getAuth(app);
  const user = auth.currentUser;

  if (!user) {
    console.log('[WebCloudFunctionsService] No authenticated user found');
    throw new Error('Authentication required - please sign in first');
  }

  return user;
};

// Helper function to get functions instance
const getFunctionsInstance = () => {
  const functions = getFunctions(app, firebaseFunctionsConfig.region);

  // IMPORTANT: For v2 web, always use production Functions.
  // The Azure AD auth flow depends on mapAzureADUser which lives in the
  // main Functions project, not the local emulator on :5100. Connecting
  // the emulator here forces all calls (including mapAzureADUser) to
  // localhost:5100 and causes CORS/preflight failures.
  console.log('[WebCloudFunctionsService] ✅ Using production Firebase Functions (emulator disabled for web)');

  return functions;
};

// Helper function to get environment parameter for Firebase Functions
const getEnvironmentParam = () => {
  return {
    environment: getEnvironment()
  };
};

export async function mapAzureADUserWeb(email: string, displayName?: string, organizationId?: string) {
  try {
    // Note: No authentication required for this function since it's used during auth process
    // IMPORTANT: Always call production Functions for Azure AD mapping.
    // The local emulator currently does not host mapAzureADUser and can cause
    // CORS/preflight failures when hit directly from the web app.
    const functions = getFunctions(app, firebaseFunctionsConfig.region);
    const mapAzureADUser = httpsCallable(functions, 'mapAzureADUser');

    console.log('[WebCloudFunctionsService] 🔄 Mapping Azure AD user:', { email, displayName, organizationId });
    const result = await mapAzureADUser({
      email,
      displayName,
      organizationId,
      ...getEnvironmentParam()
    });
    console.log('[WebCloudFunctionsService] ✅ Azure AD user mapped successfully:', result);
    return result.data;
  } catch (error) {
    console.error('[WebCloudFunctionsService] ❌ Failed to map Azure AD user:', error);
    throw error;
  }
}

export async function getChallenges(userId: string, user?: any): Promise<{
  challenges: Array<{
    id: string;
    title: string;
    description: string;
    category: string;
    level: string;
    metric: string;
    targetValue: number;
    targetUnit: string;
    measurementTarget?: number;
    measurementUnit?: string;
    points: number;
    startDate: string;
    endDate: string;
    isActive: boolean;
    emoji: string;
    type: string;
    organizationId?: string;
  }>;
  participations?: Array<any>;
}> {
  try {
    const authenticatedUser = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Calling getUserChallenges function for user:', userId);

    const fn = httpsCallable(functions, 'getUserChallenges');
    const result = await fn({
      userId,
      user,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ getUserChallenges result:', result);
    return result.data as {
      challenges: Array<{
        id: string;
        title: string;
        description: string;
        category: string;
        level: string;
        metric: string;
        targetValue: number;
        targetUnit: string;
        measurementTarget?: number;
        measurementUnit?: string;
        points: number;
        startDate: string;
        endDate: string;
        isActive: boolean;
        emoji: string;
        type: string;
        organizationId?: string;
      }>;
      participations?: Array<any>;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error calling getUserChallenges:', error);
    throw error;
  }
}

export async function getChallengesAdmin(filters?: { level?: string; organizationId?: string; isActive?: boolean }): Promise<{
  challenges: Array<{
    id: string;
    title: string;
    description: string;
    category: string;
    level: string;
    metric: string;
    targetValue: number;
    targetUnit: string;
    points: number;
    startDate: string;
    endDate: string;
    isActive: boolean;
    emoji: string;
    type: string;
    organizationId?: string;
  }>;
}> {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔍 Auth check:', {
      isAuthenticated: !!user,
      userId: user?.uid,
      userEmail: user?.email,
    });

    const fn = httpsCallable(functions, 'getChallenges');

    console.log('[WebCloudFunctionsService] 🔄 Calling getChallenges function...');
    console.log('[WebCloudFunctionsService] Function region:', firebaseFunctionsConfig.region);
    console.log('[WebCloudFunctionsService] Using emulator:', process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === 'true');
    console.log('[WebCloudFunctionsService] User ID:', user?.uid);
    console.log('[WebCloudFunctionsService] Filters:', filters);
    console.log('[WebCloudFunctionsService] Environment:', getEnvironment());

    const result = await fn({
      ...filters,
      ...getEnvironmentParam()
    });

    // Handle both response formats
    const data = result.data as any;

    if (data && data.challenges) {
      return { challenges: data.challenges };
    } else if (Array.isArray(data)) {
      // If the backend returns an array directly
      return { challenges: data };
    } else if (data && Array.isArray(data.data)) {
      // If wrapped in another data property
      return { challenges: data.data };
    }

    // Default fallback
    return { challenges: [] };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error calling getChallenges:', error);
    console.error('[WebCloudFunctionsService] Error details:', {
      code: error.code,
      message: error.message,
      details: error.details
    });
    throw error;
  }
}

export async function createChallengeAdmin(challenge: any, challengeGroup: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Creating challenge:', { challenge, challengeGroup });

    const fn = httpsCallable(functions, 'createChallenge');
    const result = await fn({
      challenge,
      challengeGroup,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Challenge created successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error creating challenge:', error);
    throw error;
  }
}

export async function updateChallengeAdmin(challengeId: string, challengeGroup: string, updates: any) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Updating challenge:', { challengeId, challengeGroup, updates });

    const fn = httpsCallable(functions, 'updateChallenge');
    const result = await fn({
      challengeId,
      challengeGroup,
      updates,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Challenge updated successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error updating challenge:', error);
    throw error;
  }
}

export async function findChallengeAcrossGroups(challengeId: string): Promise<{
  challenge: any;
  challengeGroup: string;
} | null> {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔍 Searching for challenge across all groups:', challengeId);

    // Try to find the challenge in each group
    const allGroups = ['global_challenges', 'organization_challenges', 'personal_challenges'];

    for (const group of allGroups) {
      try {
        const fn = httpsCallable(functions, 'getChallenges');
        const result = await fn({
          filters: { challengeId, group },
          ...getEnvironmentParam()
        });

        if (result.data && typeof result.data === 'object' && 'challenges' in result.data) {
          const data = result.data as any;
          if (data.challenges && data.challenges.length > 0) {
            const challenge = data.challenges.find((c: any) => c.id === challengeId);
            if (challenge) {
              console.log(`✅ Challenge ${challengeId} found in ${group}`);
              return { challenge, challengeGroup: group };
            }
          }
        }
      } catch (groupErr) {
        // Continue to next group
        console.log(`⚠️ Challenge not found in ${group}:`, groupErr);
      }
    }

    console.log(`❌ Challenge ${challengeId} not found in any group`);
    return null;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error searching for challenge:', error);
    throw error;
  }
}

export async function migrateChallengeAdmin(challengeId: string, sourceGroup: string, targetGroup: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Migrating challenge:', { challengeId, sourceGroup, targetGroup });

    const fn = httpsCallable(functions, 'migrateChallenge');
    const result = await fn({
      challengeId,
      sourceGroup,
      targetGroup,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Challenge migrated successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error migrating challenge:', error);
    throw error;
  }
}

export async function deleteChallengeAdmin(challengeId: string, challengeGroup: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Deleting challenge:', { challengeId, challengeGroup });

    const fn = httpsCallable(functions, 'deleteChallenge');
    const result = await fn({
      challengeId,
      challengeGroup,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Challenge deleted successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error deleting challenge:', error);
    throw error;
  }
}

export async function getOrganizationChallengesAdmin(organizationId: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Getting organization challenges:', { organizationId });

    const fn = httpsCallable(functions, 'getOrganizationChallenges');
    const result = await fn({
      organizationId,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Organization challenges retrieved:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting organization challenges:', error);
    throw error;
  }
}

export async function updateUserRole(userId: string, role: string) {
  try {
    await ensureAuthenticated();
    const functions = getFunctionsInstance();
    const fn = httpsCallable(functions, 'updateUserRole');

    const result = await fn({
      userId,
      role,
      ...getEnvironmentParam()
    });
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] Error calling updateUserRole:', error);
    throw error;
  }
}

export async function testAuth() {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();
    const fn = httpsCallable(functions, 'testAuth');

    console.log('[WebCloudFunctionsService] 🔄 Testing authentication...');
    const result = await fn({
      ...getEnvironmentParam()
    });
    console.log('[WebCloudFunctionsService] ✅ Auth test result:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Auth test failed:', error);
    throw error;
  }
}

export async function healthCheck() {
  try {
    const functions = getFunctionsInstance();
    const fn = httpsCallable(functions, 'healthCheck');

    console.log('[WebCloudFunctionsService] 🔄 Checking health...');
    const result = await fn({
      ...getEnvironmentParam()
    });
    console.log('[WebCloudFunctionsService] ✅ Health check result:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Health check failed:', error);
    throw error;
  }
}

export async function updateUserPassword(email: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Updating user password for:', email);

    const fn = httpsCallable(functions, 'updateUserPassword');
    const result = await fn({
      email,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Password update initiated:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error updating password:', error);
    throw error;
  }
}

/**
 * Set or update password for the currently authenticated user
 * Allows users who logged in with Azure AD to set their own password for email/password login
 */
export async function setUserPassword(password: string): Promise<{ success: boolean; message: string }> {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Setting user password');

    const fn = httpsCallable(functions, 'setUserPassword');
    const result = await fn({
      password,
    });

    console.log('[WebCloudFunctionsService] ✅ Password set successfully');
    return result.data as { success: boolean; message: string };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error setting password:', error);
    throw error;
  }
}

// ============================================================================
// BUSINESS SIGN-UP FUNCTIONS
// ============================================================================

/**
 * Initiate business sign-up
 * - Validates email domain (blocks personal email providers)
 * - Creates pending sign-up record
 * - Sends verification email
 */
export async function initiateBusinessSignUp(email: string, displayName: string): Promise<{ success: boolean; message: string }> {
  try {
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Initiating business sign-up:', { email, displayName });

    const fn = httpsCallable(functions, 'initiateBusinessSignUp');
    const result = await fn({
      email,
      displayName,
    });

    console.log('[WebCloudFunctionsService] ✅ Business sign-up initiated successfully');
    return result.data as { success: boolean; message: string };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error initiating business sign-up:', error);
    throw error;
  }
}

/**
 * Verify business sign-up token
 * - Validates token and expiration
 * - Returns user data for password setup
 */
export async function verifyBusinessSignUpToken(token: string): Promise<{
  success: boolean;
  email: string;
  displayName: string;
  token: string;
}> {
  try {
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Verifying business sign-up token');

    const fn = httpsCallable(functions, 'verifyBusinessSignUpToken');
    const result = await fn({
      token,
    });

    console.log('[WebCloudFunctionsService] ✅ Business sign-up token verified successfully');
    return result.data as {
      success: boolean;
      email: string;
      displayName: string;
      token: string;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error verifying business sign-up token:', error);
    throw error;
  }
}

/**
 * Complete business sign-up
 * - Creates Firebase Auth user with password
 * - Creates user profile
 * - Returns user data
 */
export async function completeBusinessSignUp(token: string, password: string): Promise<{
  success: boolean;
  message: string;
  userId: string;
  email: string;
  displayName: string;
}> {
  try {
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Completing business sign-up');

    const fn = httpsCallable(functions, 'completeBusinessSignUp');
    const result = await fn({
      token,
      password,
    });

    console.log('[WebCloudFunctionsService] ✅ Business sign-up completed successfully');
    return result.data as {
      success: boolean;
      message: string;
      userId: string;
      email: string;
      displayName: string;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error completing business sign-up:', error);
    throw error;
  }
}

// ============================================================================
// CUSTOM EMAIL VERIFICATION AND PASSWORD RESET
// ============================================================================

/**
 * Send custom email verification (replaces Firebase's sendEmailVerification)
 */
export async function sendCustomEmailVerification(email: string, userId?: string, displayName?: string): Promise<{
  success: boolean;
  message: string;
  alreadyVerified?: boolean;
}> {
  try {
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Sending custom email verification:', { email, userId });

    const fn = httpsCallable(functions, 'sendCustomEmailVerification');
    const result = await fn({
      email,
      userId,
      displayName,
      ...getEnvironmentParam(),
    });

    console.log('[WebCloudFunctionsService] ✅ Email verification sent successfully');
    return result.data as {
      success: boolean;
      message: string;
      alreadyVerified?: boolean;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error sending email verification:', error);
    throw error;
  }
}

/**
 * Verify email token
 */
export async function verifyEmailToken(token: string): Promise<{
  success: boolean;
  message: string;
  email: string;
  userId: string;
}> {
  try {
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Verifying email token');

    const fn = httpsCallable(functions, 'verifyEmailToken');
    const result = await fn({
      token,
    });

    console.log('[WebCloudFunctionsService] ✅ Email token verified successfully');
    return result.data as {
      success: boolean;
      message: string;
      email: string;
      userId: string;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error verifying email token:', error);
    throw error;
  }
}

/**
 * Send custom password reset email (replaces Firebase's sendPasswordResetEmail)
 */
export async function sendCustomPasswordReset(email: string): Promise<{
  success: boolean;
  message: string;
}> {
  try {
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Sending custom password reset email:', { email });

    const fn = httpsCallable(functions, 'sendCustomPasswordReset');
    const result = await fn({
      email,
      ...getEnvironmentParam(),
    });

    console.log('[WebCloudFunctionsService] ✅ Password reset email sent successfully');
    return result.data as {
      success: boolean;
      message: string;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error sending password reset email:', error);
    throw error;
  }
}

/**
 * Verify password reset token
 */
export async function verifyPasswordResetToken(token: string): Promise<{
  success: boolean;
  email: string;
  token: string;
}> {
  try {
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Verifying password reset token');

    const fn = httpsCallable(functions, 'verifyPasswordResetToken');
    const result = await fn({
      token,
    });

    console.log('[WebCloudFunctionsService] ✅ Password reset token verified successfully');
    return result.data as {
      success: boolean;
      email: string;
      token: string;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error verifying password reset token:', error);
    throw error;
  }
}

/**
 * Reset password with token
 */
export async function resetPasswordWithToken(token: string, newPassword: string): Promise<{
  success: boolean;
  message: string;
  email: string;
}> {
  try {
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Resetting password with token');

    const fn = httpsCallable(functions, 'resetPasswordWithToken');
    const result = await fn({
      token,
      newPassword,
    });

    console.log('[WebCloudFunctionsService] ✅ Password reset successfully');
    return result.data as {
      success: boolean;
      message: string;
      email: string;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error resetting password:', error);
    throw error;
  }
}

// ============================================================================
// USER MANAGEMENT CRUD OPERATIONS
// ============================================================================

export async function getUsersAdmin(filters?: {
  role?: string;
  organizationId?: string;
  isActive?: boolean;
  searchTerm?: string;
}) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔍 Getting users with filters:', filters);

    const fn = httpsCallable(functions, 'getUsers');
    const result = await fn({
      ...filters,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Users retrieved successfully:', result);
    return result.data as User[];
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting users:', error);
    throw error;
  }
}

export async function createUserAdmin(userData: {
  email: string;
  displayName: string;
  role: string;
  organizationId?: string;
  teamId?: string;
  isActive: boolean;
}) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Creating user:', userData);

    const fn = httpsCallable(functions, 'createUser');
    const result = await fn({
      ...userData,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ User created successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error creating user:', error);
    throw error;
  }
}

export async function updateUserAdmin(userId: string, updates: {
  displayName?: string;
  role?: string;
  organizationId?: string;
  teamId?: string;
  isActive?: boolean;
}) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Updating user:', { userId, updates });

    const fn = httpsCallable(functions, 'updateUser');
    const result = await fn({
      userId,
      updates,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ User updated successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error updating user:', error);
    throw error;
  }
}

export async function deleteUserAdmin(userId: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Deleting user:', userId);

    const fn = httpsCallable(functions, 'deleteUser');
    const result = await fn({
      userId,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ User deleted successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error deleting user:', error);
    throw error;
  }
}

export async function getUserStatsAdmin() {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔍 Getting user statistics');

    const fn = httpsCallable(functions, 'getUserStats');
    const result = await fn({
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ User stats retrieved:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting user stats:', error);
    throw error;
  }
}

// ============================================================================
// ORGANIZATION MANAGEMENT CRUD OPERATIONS
// ============================================================================

export async function getOrganizationsAdmin(filters?: {
  isActive?: boolean;
  searchTerm?: string;
}): Promise<Organization[]> {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔍 Getting organizations with filters:', filters);

    const fn = httpsCallable(functions, 'getOrganizations');
    const result = await fn({
      ...filters,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Organizations retrieved successfully:', result);
    return result.data as Organization[];
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting organizations:', error);
    throw error;
  }
}

export async function createOrganizationAdmin(orgData: {
  name: string;
  domain: string;
  description?: string;
  settings: {
    allowPublicJoin: boolean;
    requireApproval: boolean;
    customChallenges: boolean;
    leaderboardVisible: boolean;
    teamCreationOpen: boolean;
  };
}) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Creating organization:', orgData);

    const fn = httpsCallable(functions, 'createOrganization');
    const result = await fn({
      ...orgData,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Organization created successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error creating organization:', error);
    throw error;
  }
}

export async function updateOrganizationAdmin(orgId: string, updates: {
  name?: string;
  domain?: string;
  description?: string;
  isActive?: boolean;
  settings?: {
    allowPublicJoin?: boolean;
    requireApproval?: boolean;
    customChallenges?: boolean;
    leaderboardVisible?: boolean;
    teamCreationOpen?: boolean;
  };
}) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Updating organization:', { orgId, updates });

    const fn = httpsCallable(functions, 'updateOrganization');
    const result = await fn({
      orgId,
      updates,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Organization updated successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error updating organization:', error);
    throw error;
  }
}

export async function deleteOrganizationAdmin(orgId: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Deleting organization:', orgId);

    const fn = httpsCallable(functions, 'deleteOrganization');
    const result = await fn({
      orgId,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Organization deleted successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error deleting organization:', error);
    throw error;
  }
}

export async function getOrganizationStatsAdmin(orgId?: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔍 Getting organization statistics:', orgId);

    const fn = httpsCallable(functions, 'getOrganizationStats');
    const result = await fn({
      orgId,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Organization stats retrieved:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting organization stats:', error);
    throw error;
  }
}

// ============================================================================
// TEAM MANAGEMENT CRUD OPERATIONS
// ============================================================================

export async function getTeamsAdmin(organizationId?: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔍 Getting teams for organization:', organizationId);

    const fn = httpsCallable(functions, 'getTeams');
    const result = await fn({
      organizationId,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Teams retrieved successfully:', result);
    return result.data as Team[];
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting teams:', error);
    throw error;
  }
}

export async function createTeamAdmin(teamData: {
  name: string;
  organizationId: string;
  description?: string;
}) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Creating team:', teamData);

    const fn = httpsCallable(functions, 'createTeam');
    const result = await fn({
      ...teamData,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Team created successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error creating team:', error);
    throw error;
  }
}

export async function updateTeamAdmin(teamId: string, updates: {
  name?: string;
  description?: string;
  isActive?: boolean;
}) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Updating team:', { teamId, updates });

    const fn = httpsCallable(functions, 'updateTeam');
    const result = await fn({
      teamId,
      updates,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Team updated successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error updating team:', error);
    throw error;
  }
}

export async function deleteTeamAdmin(teamId: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Deleting team:', teamId);

    const fn = httpsCallable(functions, 'deleteTeam');
    const result = await fn({
      teamId,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Team deleted successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error deleting team:', error);
    throw error;
  }
}

// ============================================================================
// ENHANCED CHALLENGE MANAGEMENT OPERATIONS
// ============================================================================

export async function getChallengeStatsAdmin(challengeId?: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔍 Getting challenge statistics:', challengeId);

    const fn = httpsCallable(functions, 'getChallengeStats');
    const result = await fn({
      challengeId,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Challenge stats retrieved:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting challenge stats:', error);
    throw error;
  }
}

export async function duplicateChallengeAdmin(challengeId: string, newData: {
  title: string;
  startDate: string;
  endDate: string;
}) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Duplicating challenge:', { challengeId, newData });

    const fn = httpsCallable(functions, 'duplicateChallenge');
    const result = await fn({
      challengeId,
      newData,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Challenge duplicated successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error duplicating challenge:', error);
    throw error;
  }
}

export async function bulkUpdateChallengesAdmin(updates: {
  challengeIds: string[];
  updates: {
    isActive?: boolean;
    points?: number;
    targetValue?: number;
  };
}) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Bulk updating challenges:', updates);

    const fn = httpsCallable(functions, 'bulkUpdateChallenges');
    const result = await fn({
      ...updates,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Challenges bulk updated successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error bulk updating challenges:', error);
    throw error;
  }
}

export async function exportChallengesAdmin(filters?: {
  level?: string;
  organizationId?: string;
  isActive?: boolean;
  format?: 'csv' | 'json';
}) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Exporting challenges:', filters);

    const fn = httpsCallable(functions, 'exportChallenges');
    const result = await fn({
      ...filters,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Challenges exported successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error exporting challenges:', error);
    throw error;
  }
}

// ============================================================================
// SYSTEM ADMIN OPERATIONS
// ============================================================================

export async function getSystemStatsAdmin() {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔍 Getting system statistics');

    const fn = httpsCallable(functions, 'getSystemStats');
    const result = await fn({
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ System stats retrieved:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting system stats:', error);
    throw error;
  }
}

export async function clearCacheAdmin() {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Clearing system cache');

    const fn = httpsCallable(functions, 'clearCache');
    const result = await fn({ ...getEnvironmentParam() });

    console.log('[WebCloudFunctionsService] ✅ Cache cleared successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error clearing cache:', error);
    throw error;
  }
}

export async function backupDataAdmin() {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Creating data backup');

    const fn = httpsCallable(functions, 'backupData');
    const result = await fn({ ...getEnvironmentParam() });

    console.log('[WebCloudFunctionsService] ✅ Data backup created successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error creating backup:', error);
    throw error;
  }
}

// ============================================================================
// CHALLENGE OPERATIONS
// ============================================================================

export async function getChallengeProgressWeb(challengeId: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    const fn = httpsCallable(functions, 'getChallengeProgress');
    const result = await fn({
      challengeId,
      ...getEnvironmentParam()
    });

    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting challenge progress:', error);
    throw error;
  }
}

export async function joinChallenge(challengeId: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Joining challenge:', challengeId);

    const fn = httpsCallable(functions, 'joinChallenge');
    const result = await fn({ challengeId, ...getEnvironmentParam() });

    console.log('[WebCloudFunctionsService] ✅ Successfully joined challenge:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Failed to join challenge:', error);
    throw error;
  }
}

export async function updateChallengeProgressWeb(challengeId: string, value: number, date?: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Updating challenge progress:', { challengeId, value, date });

    const fn = httpsCallable(functions, 'updateChallengeProgress');
    const requestData = { challengeId, value, date, ...getEnvironmentParam() };
    console.log('[WebCloudFunctionsService] 🔄 Calling Firebase function with:', requestData);
    console.log('[WebCloudFunctionsService] 🔄 Request data JSON:', JSON.stringify(requestData));
    const result = await fn(requestData);

    console.log('[WebCloudFunctionsService] ✅ Challenge progress updated successfully:', result);
    console.log('[WebCloudFunctionsService] ✅ Result data:', result.data);
    console.log('[WebCloudFunctionsService] ✅ Result data success:', (result.data as any)?.success);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error updating challenge progress:', error);
    throw error;
  }
}

export async function getChallengeLeaderboardWeb(challengeId: string): Promise<{
  entries: Array<{
    userId: string;
    displayName: string;
    progress: number;
    currentValue: number;
    targetValue: number;
    points: number;
    rank: number;
    lastUpdated: string;
  }>;
  totalParticipants: number;
  lastUpdated: string;
}> {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 📊 Getting challenge leaderboard:', { challengeId });

    const fn = httpsCallable(functions, 'getChallengeLeaderboard');
    const result = await fn({ challengeId, ...getEnvironmentParam() });

    console.log('[WebCloudFunctionsService] ✅ Challenge leaderboard retrieved successfully:', result);
    return result.data as {
      entries: Array<{
        userId: string;
        displayName: string;
        progress: number;
        currentValue: number;
        targetValue: number;
        points: number;
        rank: number;
        lastUpdated: string;
      }>;
      totalParticipants: number;
      lastUpdated: string;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting challenge leaderboard:', error);
    throw error;
  }
}

export async function dropChallengeWeb(challengeId: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    const environmentParam = getEnvironmentParam();
    console.log('[WebCloudFunctionsService] 🚫 Dropping challenge:', {
      challengeId,
      environment: environmentParam.environment
    });

    const fn = httpsCallable(functions, 'dropChallenge');
    const result = await fn({
      challengeId,
      ...environmentParam
    });

    console.log('[WebCloudFunctionsService] ✅ Challenge dropped successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error dropping challenge:', error);
    throw error;
  }
}

export async function createUserProfile(userData: {
  displayName?: string;
  email?: string;
  organizationId?: string;
  organizationName?: string;
  role?: string;
  teamId?: string;
  teamName?: string;
  authProvider?: string;
}) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Creating user profile:', userData);

    const fn = httpsCallable(functions, 'createUserProfile');
    const result = await fn({ userData, ...getEnvironmentParam() });

    console.log('[WebCloudFunctionsService] ✅ User profile created successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error creating user profile:', error);
    throw error;
  }
}

export async function getAnalyticsData(filters?: {
  challengeId?: string;
  organizationId?: string;
  timeRange?: string;
  level?: string;
}): Promise<{
  challengeAnalytics: any | null;
  allChallenges: { [challengeId: string]: any } | null;
  systemMetrics: {
    totalChallenges: number;
    activeChallenges: number;
    completedChallenges: number;
    totalParticipants: number;
    totalPointsEarned: number;
  };
  averageEngagement: number;
  systemTopPerformers: Array<{
    userId: string;
    displayName: string;
    averageProgress: number;
    participationsCount: number;
  }>;
  dailyTrends: Array<{
    date: string;
    completions: number;
    participants: number;
    averageProgress: number;
  }>;
  filters: { challengeId?: string; organizationId?: string; timeRange: string; level?: string };
  generatedAt: string;
}> {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 📊 Getting analytics data:', filters);

    const fn = httpsCallable(functions, 'getAnalyticsData');
    const result = await fn({
      ...filters,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Analytics data retrieved successfully:', result);

    // Firebase Functions onCall automatically wraps return value in {data: ...}
    // So result.data contains the actual return value from the function
    const analyticsResult = result.data;

    // Log the structure for debugging
    console.log('[WebCloudFunctionsService] 📊 Analytics result structure:', {
      hasData: !!analyticsResult,
      keys: analyticsResult ? Object.keys(analyticsResult) : [],
      hasSystemMetrics: !!(analyticsResult as any)?.systemMetrics,
      hasChallengeAnalytics: !!(analyticsResult as any)?.challengeAnalytics,
      hasAllChallenges: !!(analyticsResult as any)?.allChallenges,
    });

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/dd95de8b-4aae-4806-ab49-a2b65a05ac50', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: 'debug-session',
        runId: 'run1',
        hypothesisId: 'H2',
        location: 'web/src/services/WebCloudFunctionsService.ts:getAnalyticsData',
        message: 'Web getAnalyticsData result',
        data: {
          filters,
          hasData: !!analyticsResult,
          keys: analyticsResult ? Object.keys(analyticsResult) : [],
          hasSystemMetrics: !!(analyticsResult as any)?.systemMetrics,
          hasChallengeAnalytics: !!(analyticsResult as any)?.challengeAnalytics,
          hasAllChallenges: !!(analyticsResult as any)?.allChallenges,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => { });
    // #endregion agent log

    return analyticsResult as {
      challengeAnalytics: any | null;
      allChallenges: { [challengeId: string]: any } | null;
      systemMetrics: {
        totalChallenges: number;
        activeChallenges: number;
        completedChallenges: number;
        totalParticipants: number;
        totalPointsEarned: number;
      };
      averageEngagement: number;
      systemTopPerformers: Array<{
        userId: string;
        displayName: string;
        averageProgress: number;
        participationsCount: number;
      }>;
      dailyTrends: Array<{
        date: string;
        completions: number;
        participants: number;
        averageProgress: number;
      }>;
      filters: { challengeId?: string; organizationId?: string; timeRange: string; level?: string };
      generatedAt: string;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting analytics data:', error);
    throw error;
  }
}

// New aggregated analytics function for better performance
export async function getAggregatedAnalytics(filters?: {
  challengeId?: string;
  organizationId?: string;
  timeRange?: string;
  level?: string;
  useCache?: boolean;
}): Promise<{
  id: string;
  type: 'daily' | 'weekly' | 'monthly';
  date: string;
  organizationId?: string;
  challengeId?: string;
  metrics: {
    totalParticipants: number;
    totalCompletions: number;
    totalAttempts: number;
    averageProgress: number;
    totalPoints: number;
    activeUsers: number;
  };
  challengeMetrics: {
    totalChallenges: number;
    activeChallenges: number;
    completedChallenges: number;
    averagePoints: number;
  };
  topPerformers: Array<{
    userId: string;
    displayName: string;
    progress: number;
    points: number;
    lastActivity: string;
  }>;
  lastUpdated: string;
  dataVersion: number;
}> {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 📊 Getting aggregated analytics data:', filters);

    const fn = httpsCallable(functions, 'getAggregatedAnalytics');
    const result = await fn({ ...filters, ...getEnvironmentParam() });

    console.log('[WebCloudFunctionsService] ✅ Aggregated analytics data retrieved successfully:', result);
    return result.data as {
      id: string;
      type: 'daily' | 'weekly' | 'monthly';
      date: string;
      organizationId?: string;
      challengeId?: string;
      metrics: {
        totalParticipants: number;
        totalCompletions: number;
        totalAttempts: number;
        averageProgress: number;
        totalPoints: number;
        activeUsers: number;
      };
      challengeMetrics: {
        totalChallenges: number;
        activeChallenges: number;
        completedChallenges: number;
        averagePoints: number;
      };
      topPerformers: Array<{
        userId: string;
        displayName: string;
        progress: number;
        points: number;
        lastActivity: string;
      }>;
      lastUpdated: string;
      dataVersion: number;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting aggregated analytics data:', error);
    throw error;
  }
}

// Analytics maintenance functions
export async function invalidateAnalyticsCache(params?: {
  challengeId?: string;
  organizationId?: string;
  reason?: string;
}): Promise<{ success: boolean; message: string }> {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Invalidating analytics cache:', params);

    const fn = httpsCallable(functions, 'invalidateAnalyticsCache');
    const result = await fn({ ...params, ...getEnvironmentParam() });

    console.log('[WebCloudFunctionsService] ✅ Analytics cache invalidated successfully:', result);
    return result.data as { success: boolean; message: string };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error invalidating analytics cache:', error);
    throw error;
  }
}

export async function scheduleAnalyticsUpdate(params?: {
  updateType?: string;
}): Promise<{ success: boolean; message: string }> {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] ⏰ Scheduling analytics update:', params);

    const fn = httpsCallable(functions, 'scheduleAnalyticsUpdate');
    const result = await fn({ ...params, ...getEnvironmentParam() });

    console.log('[WebCloudFunctionsService] ✅ Analytics update scheduled successfully:', result);
    return result.data as { success: boolean; message: string };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error scheduling analytics update:', error);
    throw error;
  }
}

// ============================================================================
// ORGANIZATION AZURE AD CONFIGURATION
// ============================================================================

export interface OrganizationAzureADConfig {
  enabled: boolean;
  clientId: string;
  tenantId: string;
  redirectUri?: string;
  scopes?: string[];
  updatedAt?: string;
  updatedBy?: string;
}

export async function saveOrganizationAzureADConfig(
  organizationId: string,
  azureADConfig: OrganizationAzureADConfig
): Promise<{ success: boolean; message: string }> {
  try {
    await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Saving organization Azure AD config:', { organizationId, azureADConfig });

    const fn = httpsCallable(functions, 'saveOrganizationAzureADConfig');
    const result = await fn({
      organizationId,
      azureADConfig,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Organization Azure AD config saved successfully:', result);
    return result.data as { success: boolean; message: string };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error saving organization Azure AD config:', error);
    throw error;
  }
}

export async function getOrganizationAzureADConfig(
  organizationId: string
): Promise<{ success: boolean; azureADConfig: OrganizationAzureADConfig | null }> {
  try {
    await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔍 Getting organization Azure AD config:', organizationId);

    const fn = httpsCallable(functions, 'getOrganizationAzureADConfig');
    const result = await fn({
      organizationId,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Organization Azure AD config retrieved successfully:', result);
    return result.data as { success: boolean; azureADConfig: OrganizationAzureADConfig | null };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting organization Azure AD config:', error);
    throw error;
  }
}

export async function getDailyCompletionsData(date?: string, challengeId?: string): Promise<Array<{
  userId: string;
  displayName: string;
  challengeId: string;
  challengeName: string;
  challengeEmoji: string;
  completedAt: string;
  points: number;
  value: number;
  targetValue: number;
  isCompleted: boolean;
}>> {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 📅 Getting daily completions data:', { date, challengeId });

    const fn = httpsCallable(functions, 'getDailyCompletionsData');
    const result = await fn({
      challengeId,
      timeRange: date ? 'custom' : '30d',
      startDate: date,
      endDate: date,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Daily completions data retrieved successfully:', result);

    // Extract completion details from the response
    const responseData = result.data as {
      dailyData: Array<{
        date: string;
        completionDetails: Array<{
          userId: string;
          displayName: string;
          challengeId: string;
          challengeName: string;
          challengeEmoji: string;
          completedAt: string;
          points: number;
          value: number;
          targetValue: number;
          isCompleted: boolean;
        }>;
      }>;
    };

    // If a specific date is requested, return only that date's completions
    if (date && responseData.dailyData) {
      const targetDay = responseData.dailyData.find(day => day.date === date);
      return targetDay?.completionDetails || [];
    }

    // Otherwise, return all completions from all days
    const allCompletions = responseData.dailyData?.flatMap(day => day.completionDetails) || [];
    return allCompletions;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting daily completions data:', error);
    console.log('[WebCloudFunctionsService] 📅 Using mock daily completions data since backend function is not available');

    // Return mock data for now
    return [
      {
        userId: 'user1',
        displayName: 'John Doe',
        challengeId: 'challenge1',
        challengeName: 'Daily Walking',
        challengeEmoji: '🚶',
        completedAt: new Date().toISOString(),
        points: 10,
        value: 8000,
        targetValue: 10000,
        isCompleted: true,
      },
      {
        userId: 'user2',
        displayName: 'Jane Smith',
        challengeId: 'challenge1',
        challengeName: 'Daily Walking',
        challengeEmoji: '🚶',
        completedAt: new Date().toISOString(),
        points: 10,
        value: 12000,
        targetValue: 10000,
        isCompleted: true,
      },
    ];
  }
}

/**
 * Get global leaderboard data across all challenges
 * @param {Object} filters - Filter options for leaderboard
 * @returns {Promise<Object>} Global leaderboard data
 */
export async function getGlobalLeaderboardWeb(filters?: {
  challengeId?: string;
  organizationId?: string;
  timeRange?: string;
  limit?: number;
  sortBy?: 'points' | 'progress' | 'completion' | 'overall';
}): Promise<{
  entries: Array<{
    userId: string;
    displayName: string;
    profileImageURL?: string;
    organizationId?: string;
    organizationName?: string;
    totalPoints: number;
    averageProgress: number;
    completionRate: number;
    challengesParticipated: number;
    completedChallenges: number;
    totalValue: number;
    totalTargetValue: number;
    overallProgress: number;
    rank: number;
  }>;
  totalParticipants: number;
  filters: any;
  generatedAt: string;
}> {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🏆 Getting global leaderboard:', filters);

    const fn = httpsCallable(functions, 'getGlobalLeaderboard');
    const result = await fn({ ...(filters || {}), ...getEnvironmentParam() });

    console.log('[WebCloudFunctionsService] ✅ Global leaderboard retrieved successfully:', result);
    return result.data as {
      entries: Array<{
        userId: string;
        displayName: string;
        profileImageURL?: string;
        organizationId?: string;
        organizationName?: string;
        totalPoints: number;
        averageProgress: number;
        completionRate: number;
        challengesParticipated: number;
        completedChallenges: number;
        totalValue: number;
        totalTargetValue: number;
        overallProgress: number;
        rank: number;
      }>;
      totalParticipants: number;
      filters: any;
      generatedAt: string;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting global leaderboard:', error);
    throw error;
  }
}

/**
 * Get detailed daily completions data for analytics
 * @param {Object} filters - Filter options for daily completions
 * @returns {Promise<Object>} Daily completions data
 */
export async function getDailyCompletionsDataWeb(filters?: {
  challengeId?: string;
  organizationId?: string;
  timeRange?: string;
  userId?: string;
  exportFormat?: string;
  startDate?: string;
  endDate?: string;
}): Promise<{
  dailyData: Array<{
    date: string;
    formattedDate: string;
    completions: number;
    attempts: number;
    participants: number;
    totalPoints: number;
    totalAttemptPoints: number;
    averagePoints: number;
    averageAttemptPoints: number;
    completionDetails: Array<{
      userId: string;
      displayName: string;
      challengeId: string;
      challengeName: string;
      challengeEmoji: string;
      completedAt: string;
      points: number;
      value: number;
      targetValue: number;
      isCompleted: boolean;
    }>;
    attemptDetails: Array<{
      userId: string;
      displayName: string;
      challengeId: string;
      challengeName: string;
      challengeEmoji: string;
      completedAt: string;
      points: number;
      value: number;
      targetValue: number;
      isCompleted: boolean;
    }>;
  }>;
  summary: {
    totalDays: number;
    totalSignups: number;
    totalAttempts: number;
    totalCompletions: number;
    totalParticipants: number;
    totalPoints: number;
    totalAttemptPoints: number;
    averageCompletionsPerDay: number;
    averageAttemptsPerDay: number;
    peakActivityDay: any;
  };
  filters: any;
  generatedAt: string;
}> {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 📅 Getting daily completions data:', filters);

    const fn = httpsCallable(functions, 'getDailyCompletionsData');
    const result = await fn({ ...(filters || {}), ...getEnvironmentParam() });

    console.log('[WebCloudFunctionsService] ✅ Daily completions data retrieved successfully:', result);
    return result.data as {
      dailyData: Array<{
        date: string;
        formattedDate: string;
        completions: number;
        attempts: number;
        participants: number;
        totalPoints: number;
        totalAttemptPoints: number;
        averagePoints: number;
        averageAttemptPoints: number;
        completionDetails: Array<{
          userId: string;
          displayName: string;
          challengeId: string;
          challengeName: string;
          challengeEmoji: string;
          completedAt: string;
          points: number;
          value: number;
          targetValue: number;
          isCompleted: boolean;
        }>;
        attemptDetails: Array<{
          userId: string;
          displayName: string;
          challengeId: string;
          challengeName: string;
          challengeEmoji: string;
          completedAt: string;
          points: number;
          value: number;
          targetValue: number;
          isCompleted: boolean;
        }>;
      }>;
      summary: {
        totalDays: number;
        totalSignups: number;
        totalAttempts: number;
        totalCompletions: number;
        totalParticipants: number;
        totalPoints: number;
        totalAttemptPoints: number;
        averageCompletionsPerDay: number;
        averageAttemptsPerDay: number;
        peakActivityDay: any;
      };
      filters: any;
      generatedAt: string;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting daily completions data:', error);
    throw error;
  }
}

// ============================================================================
// FAMILY MEMBER MANAGEMENT FUNCTIONS
// ============================================================================

export async function inviteFamilyMemberWeb(email: string, displayName?: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Inviting family member:', { email, displayName });

    const fn = httpsCallable(functions, 'inviteFamilyMember');
    const result = await fn({
      email,
      displayName: displayName || email.split('@')[0],
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Family member invited successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error inviting family member:', error);
    throw error;
  }
}

export async function acceptFamilyInvitationWeb(invitationId: string, token: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Accepting family invitation:', { invitationId, token });

    const fn = httpsCallable(functions, 'acceptFamilyInvitation');
    const result = await fn({
      invitationId,
      token,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Family invitation accepted successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error accepting family invitation:', error);
    throw error;
  }
}

export async function getFamilyMembersWeb(orgUserId: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    const environmentParam = getEnvironmentParam();
    console.log('[WebCloudFunctionsService] 🔍 Getting family members for org user:', orgUserId);
    console.log('[WebCloudFunctionsService] 🔍 Environment param:', environmentParam);

    const fn = httpsCallable(functions, 'getFamilyMembers');
    const result = await fn({
      orgUserId,
      ...environmentParam
    });

    console.log('[WebCloudFunctionsService] ✅ Family members retrieved successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting family members:', error);
    throw error;
  }
}

export async function removeFamilyMemberWeb(familyMemberId: string, orgUserId: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Removing family member:', { familyMemberId, orgUserId });

    const fn = httpsCallable(functions, 'removeFamilyMember');
    const result = await fn({
      familyMemberId,
      orgUserId,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Family member removed successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error removing family member:', error);
    throw error;
  }
}

export async function resendFamilyInvitationWeb(invitationId: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Resending family invitation:', { invitationId });

    const fn = httpsCallable(functions, 'resendFamilyInvitation');
    const result = await fn({
      invitationId,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Family invitation resent successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error resending family invitation:', error);
    throw error;
  }
}

export async function cancelFamilyInvitationWeb(invitationId: string) {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Cancelling family invitation:', { invitationId });

    const fn = httpsCallable(functions, 'cancelFamilyInvitation');
    const result = await fn({
      invitationId,
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Family invitation cancelled successfully:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error cancelling family invitation:', error);
    throw error;
  }
}

/**
 * Get efficient challenge leaderboard data (pre-calculated, no real-time computation)
 * @param {Object} params - Parameters for leaderboard
 * @returns {Promise<Object>} Challenge leaderboard data
 */
export async function getChallengeLeaderboardEfficient(params: {
  organizationId: string;
  challengeType: string;
  challengeId: string;
  limit?: number;
}): Promise<{
  rankings: Array<{
    userId: string;
    displayName: string;
    profileImageURL?: string;
    currentScore: number;
    totalPoints: number;
    progress: number;
    lastUpdated: string;
    rank: number;
  }>;
  metadata: {
    totalParticipants: number;
    averageScore: number;
    averageProgress: number;
    lastCalculated: string;
  };
  filters: any;
  generatedAt: string;
}> {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🏆 Getting efficient challenge leaderboard:', params);

    const fn = httpsCallable(functions, 'getChallengeLeaderboardEfficient');
    const result = await fn({ ...params, ...getEnvironmentParam() });

    console.log('[WebCloudFunctionsService] ✅ Efficient challenge leaderboard retrieved successfully:', result);
    return result.data as {
      rankings: Array<{
        userId: string;
        displayName: string;
        profileImageURL?: string;
        currentScore: number;
        totalPoints: number;
        progress: number;
        lastUpdated: string;
        rank: number;
      }>;
      metadata: {
        totalParticipants: number;
        averageScore: number;
        averageProgress: number;
        lastCalculated: string;
      };
      filters: any;
      generatedAt: string;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting efficient challenge leaderboard:', error);
    throw error;
  }
}

/**
 * Get challenge analytics data from historical snapshots
 * @param {Object} params - Parameters for analytics
 * @returns {Promise<Object>} Challenge analytics data
 */
export async function getChallengeAnalytics(params: {
  organizationId: string;
  challengeType: string;
  challengeId: string;
  timeRange?: '7d' | '30d' | '90d';
}): Promise<{
  dailyData: Array<{
    date: string;
    formattedDate: string;
    totalParticipants: number;
    activeParticipants: number;
    totalCompletions: number;
    totalAttempts: number;
    averageScore: number;
    averageProgress: number;
    totalPoints: number;
  }>;
  summary: {
    totalDays: number;
    totalSignups: number;
    totalAttempts: number;
    totalCompletions: number;
    totalParticipants: number;
    totalPoints: number;
    averageScore: number;
    averageProgress: number;
  };
  filters: any;
  generatedAt: string;
}> {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 📊 Getting challenge analytics:', params);

    const fn = httpsCallable(functions, 'getChallengeAnalytics');
    const result = await fn({ ...params, ...getEnvironmentParam() });

    console.log('[WebCloudFunctionsService] ✅ Challenge analytics retrieved successfully:', result);
    return result.data as {
      dailyData: Array<{
        date: string;
        formattedDate: string;
        totalParticipants: number;
        activeParticipants: number;
        totalCompletions: number;
        totalAttempts: number;
        averageScore: number;
        averageProgress: number;
        totalPoints: number;
      }>;
      summary: {
        totalDays: number;
        totalSignups: number;
        totalAttempts: number;
        totalCompletions: number;
        totalParticipants: number;
        totalPoints: number;
        averageScore: number;
        averageProgress: number;
      };
      filters: any;
      generatedAt: string;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error getting challenge analytics:', error);
    throw error;
  }
}

/**
 * Update leaderboard when a user submits a completion
 * @param {Object} params - Completion data
 * @returns {Promise<Object>} Update result
 */
export async function updateLeaderboardOnCompletion(params: {
  userId: string;
  challengeId: string;
  challengeType: string;
  organizationId: string;
}): Promise<{
  success: boolean;
  message: string;
}> {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🏆 Updating leaderboard on completion:', params);

    const fn = httpsCallable(functions, 'updateLeaderboardOnCompletion');
    const result = await fn({ ...params, ...getEnvironmentParam() });

    console.log('[WebCloudFunctionsService] ✅ Leaderboard updated successfully:', result);
    return result.data as {
      success: boolean;
      message: string;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error updating leaderboard on completion:', error);
    throw error;
  }
}

/**
 * Batch update all leaderboards for an organization
 * @param {Object} params - Organization parameters
 * @returns {Promise<Object>} Batch update result
 */
export async function updateAllOrganizationLeaderboards(params: {
  organizationId: string;
}): Promise<{
  success: boolean;
  totalChallenges: number;
  successful: number;
  failed: number;
  message: string;
}> {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🏆 Batch updating organization leaderboards:', params);

    const fn = httpsCallable(functions, 'updateAllOrganizationLeaderboards');
    const result = await fn({ ...params, ...getEnvironmentParam() });

    console.log('[WebCloudFunctionsService] ✅ Organization leaderboards updated successfully:', result);
    return result.data as {
      success: boolean;
      totalChallenges: number;
      successful: number;
      failed: number;
      message: string;
    };
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error batch updating organization leaderboards:', error);
    throw error;
  }
}

export async function leaveFamilyWeb() {
  try {
    const user = await ensureAuthenticated();
    const functions = getFunctionsInstance();

    console.log('[WebCloudFunctionsService] 🔄 Leaving family for user:', user.uid);

    const fn = httpsCallable(functions, 'leaveFamily');
    const result = await fn({
      ...getEnvironmentParam()
    });

    console.log('[WebCloudFunctionsService] ✅ Successfully left family:', result);
    return result.data;
  } catch (error: any) {
    console.error('[WebCloudFunctionsService] ❌ Error leaving family:', error);
    throw error;
  }
}

